import { IFunctionalTagOption } from 'transfix-ui/components/FunctionalTag'
import { TAG_STYLE } from 'transfix-ui/components/Tag'
import { $enum } from 'ts-enum-util'
import { LOAD_STATE, STOP_TYPE } from 'apps/fleet-planner/constants/loads'
import { IStop } from 'apps/fleet-planner/types/loads'
import { capitalize } from 'utils/capitalize'

// This is necessary in order to display the different statuses options in a specific order
// You can change this as necessary, this is entirely presentational (not captured on the BE)
// More details on stop sequences: https://www.figma.com/file/8LGbzQ4ihbAHWACIDyD0Or/Fleet-Planner-V1?node-id=14941%3A799
export const getStatusOrder = (
  loadStatusKey: keyof typeof LOAD_STATE,
  stopSequence?: number
): string => {
  switch (LOAD_STATE[loadStatusKey]) {
    case LOAD_STATE.Pending:
      return '00'
    case LOAD_STATE['Ready for Dispatch']:
      return '11'
    case LOAD_STATE.Dispatched:
      return '22'
    case LOAD_STATE['In Yard']:
      return '33'
    case LOAD_STATE['In Transit']:
      return `4${stopSequence}1`
    case LOAD_STATE['At Pickup']:
      return `4${stopSequence}2`
    case LOAD_STATE.Loading:
      return `4${stopSequence}3`
    case LOAD_STATE['At Dropoff']:
      return `4${stopSequence}4`
    case LOAD_STATE.Unloading:
      return `4${stopSequence}5`
    case LOAD_STATE.Delivered:
      return '55'
    case LOAD_STATE.Invoiced:
      return '60'
    case LOAD_STATE.Completed:
      return '66'
    case LOAD_STATE.Cancelled:
      return '77'
    case LOAD_STATE.TONU:
      return '99'
    case LOAD_STATE.Claim:
      return '99'
    default:
      return '00'
  }
}

export const getLoadStatusTagStyle = (loadStatusKey: keyof typeof LOAD_STATE): TAG_STYLE => {
  switch (LOAD_STATE[loadStatusKey]) {
    case LOAD_STATE.Cancelled:
    case LOAD_STATE.TONU:
    case LOAD_STATE.Claim:
      return TAG_STYLE.error
    case LOAD_STATE.Completed:
      return TAG_STYLE.success
    case LOAD_STATE['POD Rejected']:
      return TAG_STYLE.warning
    default:
      return TAG_STYLE.info
  }
}

export const getStopPrefix = (
  loadStatusKey: keyof typeof LOAD_STATE,
  stopType: STOP_TYPE
): string => {
  switch (LOAD_STATE[loadStatusKey]) {
    case LOAD_STATE.Loading:
    case LOAD_STATE.Unloading:
      return `at ${capitalize(stopType)}`
    case LOAD_STATE['In Transit']:
      return `to ${capitalize(stopType)}`
    default:
      return ''
  }
}

const shouldIncludeSequence = (loadStatusKey: keyof typeof LOAD_STATE) =>
  [
    LOAD_STATE['At Pickup'],
    LOAD_STATE.Loading,
    LOAD_STATE['In Transit'],
    LOAD_STATE['At Dropoff'],
    LOAD_STATE.Unloading,
  ].includes(LOAD_STATE[loadStatusKey])

export const getLoadStatusTagLabel = (
  loadStatusKey: keyof typeof LOAD_STATE,
  stopType: STOP_TYPE,
  stopIndex: number = 0,
  nbOfStopsOfType: number
): string =>
  `${loadStatusKey}${` ${getStopPrefix(loadStatusKey, stopType)}`}${
    shouldIncludeSequence(loadStatusKey) && nbOfStopsOfType > 1 ? ` ${stopIndex + 1}` : ''
  }`

export const getLoadStatusOptionValue = (
  loadStatusKey: keyof typeof LOAD_STATE,
  stopSequence: number = 0
): string =>
  `${getStatusOrder(loadStatusKey, stopSequence)}:${LOAD_STATE[loadStatusKey]}${
    shouldIncludeSequence(loadStatusKey) ? `:${stopSequence}` : ''
  }`

export const createLoadStatusOption = (
  loadStatusKey: keyof typeof LOAD_STATE,
  stopType: STOP_TYPE,
  stopIndex: number,
  stopSequence: number,
  nbOfStops: number
): IFunctionalTagOption<string> => ({
  label: getLoadStatusTagLabel(loadStatusKey, stopType, stopIndex, nbOfStops),
  value: getLoadStatusOptionValue(loadStatusKey, stopSequence),
  style: getLoadStatusTagStyle(loadStatusKey),
  outlineOnly: LOAD_STATE[loadStatusKey] === LOAD_STATE.Pending,
})

export const filterPickupStops = (stops: IStop[] = []): IStop[] => {
  return stops
    .filter(stop => stop?.stopType === STOP_TYPE.pickup)
    .sort((a, b) => a.stopSequence - b.stopSequence)
}

export const filterDropoffStops = (stops: IStop[] = []): IStop[] => {
  return stops
    .filter(stop => stop?.stopType === STOP_TYPE.dropoff)
    .sort((a, b) => a.stopSequence - b.stopSequence)
}

export const getOptions = ({
  pickupStops = [],
  dropoffStops = [],
}: {
  pickupStops: IStop[]
  dropoffStops: IStop[]
}): IFunctionalTagOption<string>[] => {
  const statusOptions: IFunctionalTagOption<string>[] = []
  $enum(LOAD_STATE)
    .getKeys()
    .forEach(key => {
      if (LOAD_STATE[key] === LOAD_STATE['At Pickup']) {
        pickupStops.forEach((stop: IStop, index: number) => {
          statusOptions.push(
            createLoadStatusOption(
              key,
              STOP_TYPE.pickup,
              index,
              stop.stopSequence,
              pickupStops.length
            )
          )
        })
      } else if (LOAD_STATE[key] === LOAD_STATE.Loading) {
        pickupStops.forEach((stop: IStop, index: number) => {
          statusOptions.push(
            createLoadStatusOption(
              key,
              STOP_TYPE.pickup,
              index,
              stop.stopSequence,
              pickupStops.length
            )
          )
        })
      } else if (LOAD_STATE[key] === LOAD_STATE['In Transit']) {
        pickupStops.forEach((stop: IStop, index: number) => {
          if (stop.stopSequence !== 1) {
            statusOptions.push(
              createLoadStatusOption(
                key,
                STOP_TYPE.pickup,
                index,
                stop.stopSequence,
                pickupStops.length
              )
            )
          }
        })
        dropoffStops.forEach((stop: IStop, index: number) => {
          statusOptions.push(
            createLoadStatusOption(
              key,
              STOP_TYPE.dropoff,
              index,
              stop.stopSequence,
              dropoffStops.length
            )
          )
        })
      } else if (LOAD_STATE[key] === LOAD_STATE['At Dropoff']) {
        dropoffStops.forEach((stop: IStop, index: number) => {
          statusOptions.push(
            createLoadStatusOption(
              key,
              STOP_TYPE.dropoff,
              index,
              stop.stopSequence,
              dropoffStops.length
            )
          )
        })
      } else if (LOAD_STATE[key] === LOAD_STATE.Unloading) {
        dropoffStops.forEach((stop: IStop, index: number) => {
          statusOptions.push(
            createLoadStatusOption(
              key,
              STOP_TYPE.dropoff,
              index,
              stop.stopSequence,
              dropoffStops.length
            )
          )
        })
      } else if (
        [
          LOAD_STATE.Pending,
          LOAD_STATE['Ready for Dispatch'],
          LOAD_STATE.Dispatched,
          LOAD_STATE['In Yard'],
          LOAD_STATE.Delivered,
          LOAD_STATE.Invoiced,
          LOAD_STATE.Completed,
          LOAD_STATE.Cancelled,
          LOAD_STATE.TONU,
          LOAD_STATE.Claim,
        ].includes(LOAD_STATE[key])
      ) {
        statusOptions.push({
          label: key,
          value: getLoadStatusOptionValue(key),
          style: getLoadStatusTagStyle(key),
          outlineOnly: LOAD_STATE[key] === LOAD_STATE.Pending,
        })
      }
    })

  return statusOptions.sort((a, b) => (a.value > b.value ? 1 : -1))
}

export const getMarketplaceOptions = ({
  pickupStops = [],
  dropoffStops = [],
}: {
  pickupStops: IStop[]
  dropoffStops: IStop[]
}): IFunctionalTagOption<string>[] => {
  const statusOptions: IFunctionalTagOption<string>[] = []
  $enum(LOAD_STATE)
    .getKeys()
    .forEach(key => {
      if (LOAD_STATE[key] === LOAD_STATE['At Pickup']) {
        pickupStops.forEach((stop: IStop, index: number) => {
          statusOptions.push(
            createLoadStatusOption(
              key,
              STOP_TYPE.pickup,
              index,
              stop.stopSequence,
              pickupStops.length
            )
          )
        })
      } else if (LOAD_STATE[key] === LOAD_STATE['In Transit']) {
        pickupStops.forEach((stop: IStop, index: number) => {
          if (stop.stopSequence !== 1) {
            statusOptions.push(
              createLoadStatusOption(
                key,
                STOP_TYPE.pickup,
                index,
                stop.stopSequence,
                pickupStops.length
              )
            )
          }
        })
        dropoffStops.forEach((stop: IStop, index: number) => {
          statusOptions.push(
            createLoadStatusOption(
              key,
              STOP_TYPE.dropoff,
              index,
              stop.stopSequence,
              dropoffStops.length
            )
          )
        })
      } else if (LOAD_STATE[key] === LOAD_STATE['At Dropoff']) {
        dropoffStops.forEach((stop: IStop, index: number) => {
          statusOptions.push(
            createLoadStatusOption(
              key,
              STOP_TYPE.dropoff,
              index,
              stop.stopSequence,
              dropoffStops.length
            )
          )
        })
      } else if (
        [LOAD_STATE['Ready for Dispatch'], LOAD_STATE.Dispatched, LOAD_STATE.Delivered].includes(
          LOAD_STATE[key]
        )
      ) {
        statusOptions.push({
          label: key,
          value: getLoadStatusOptionValue(key),
          style: getLoadStatusTagStyle(key),
        })
      }
    })

  return statusOptions.sort((a, b) => (a.value > b.value ? 1 : -1))
}

export interface IFilterStopsByTypeProps {
  stops: IStop[]
  currentStopSequence: number
}

export const filterStopsByType = ({
  stops = [],
  currentStopSequence = 0,
}: IFilterStopsByTypeProps): {
  stopsOfCurrentStopType: IStop[]
  currentStopIndex: number
  currentStopType: STOP_TYPE
} => {
  const [defaultStop] = stops || []
  const selectedStop = stops?.filter(s => s?.stopSequence === currentStopSequence)[0]
  const { id: currentStopId, stopType: currentStopType } = selectedStop || defaultStop || {}
  const stopsOfCurrentStopType = stops?.filter(s => s?.stopType === currentStopType)
  const currentStopIndex = stopsOfCurrentStopType.findIndex(s => s?.id === currentStopId)
  return {
    stopsOfCurrentStopType,
    currentStopIndex,
    currentStopType,
  }
}

export const EDITABLE_MP_STATES = [
  LOAD_STATE['Ready for Dispatch'],
  LOAD_STATE.Dispatched,
  LOAD_STATE['At Pickup'],
  LOAD_STATE['In Transit'],
  LOAD_STATE['At Dropoff'],
  LOAD_STATE.Delivered,
]

export const getMarketplaceTag = (
  loadStatusKey: LOAD_STATE
): { label: keyof typeof LOAD_STATE; style: TAG_STYLE } => {
  const key = $enum(LOAD_STATE).getKeyOrThrow(loadStatusKey)
  return {
    label: key,
    style: getLoadStatusTagStyle(key),
  }
}
